import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

// Whether the primary input can genuinely hover (i.e. a real mouse/trackpad).
// False on touch-only devices. Touch browsers can leave an element matching
// `:hover` indefinitely after a tap/swipe - there's no mouseleave to clear
// it - so `:hover` alone isn't a reliable "should autoplay pause" signal on
// mobile; it must be paired with this check (see play()).
const supportsHover = matchMedia('(hover: hover)');

/**
 * Attempts to play a video, retrying via requestAnimationFrame if the
 * browser rejects the call instead of giving up on the first rejection.
 *
 * Confirmed directly against this theme's hero video (live, via devtools):
 * Chrome - and the same underlying mechanism on Android Chrome, plus an
 * equivalent WebKit behavior on iOS Safari - can reject play() with
 * `AbortError: ...paused to save power` when it's called at the exact
 * moment a slide becomes current, i.e. right as the scroll-snap transition
 * finishes settling - before the browser's own visibility bookkeeping for
 * that video has caught up (see
 * https://developer.chrome.com/blog/play-request-was-interrupted/). The
 * video isn't actually ineligible to play - calling play() again a moment
 * later succeeds immediately - so this is purely a timing race. Silently
 * swallowing that rejection (as this code used to) left the video paused
 * forever, since nothing else was left to re-trigger it, on both a page
 * reload (this runs very early, before layout has settled) and a genuine
 * slide change (this runs synchronously mid-transition). A short
 * rAF-delayed retry reliably wins that race. Bounded to a handful of
 * attempts so a video that's genuinely not meant to play (e.g. removed
 * from the DOM, or blocked for an unrelated reason) doesn't retry forever.
 * @param {HTMLVideoElement} video
 * @param {number} [attemptsLeft]
 */
function attemptPlayVideo(video, attemptsLeft = 5) {
  video.play().catch(() => {
    if (attemptsLeft <= 0 || !video.isConnected) return;
    requestAnimationFrame(() => attemptPlayVideo(video, attemptsLeft - 1));
  });
}

/**
 * Overlays a "tap to play" button on a video and keeps its visibility in
 * sync with the video's actual paused/playing state. Idempotent - safe to
 * call every time a slide becomes current, only sets up the button once per
 * video (tracked via a dataset flag).
 *
 * iOS Safari in Low Power Mode refuses muted background-video autoplay
 * outright - no amount of retrying play() from JS (see attemptPlayVideo
 * above) will ever succeed, since it isn't a timing race there, it's a
 * deliberate block. Safari used to paper over this with its own native
 * tap-to-play overlay in that situation, but that native fallback stopped
 * reliably appearing once this file started retrying play() aggressively -
 * the repeated calls seem to reset whatever internal state Safari uses to
 * decide when to surface its own affordance, so it just never showed up,
 * leaving Low Power Mode visitors with a permanently frozen video and no
 * way to start it themselves. Rather than depend on that unpredictable
 * native behavior, this gives every hero video its own first-party
 * fallback: whenever the video is paused, this button is visible; tapping
 * it calls play() directly from a real click event, which - unlike a
 * JS-initiated call - is never subject to autoplay/power-saving
 * restrictions, so it always works regardless of Low Power Mode.
 * @param {HTMLVideoElement} video
 */
function ensureVideoPlayFallback(video) {
  if (video.dataset.hasPlayFallback) return;
  video.dataset.hasPlayFallback = 'true';

  const wrapper = video.parentElement;
  if (!wrapper) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'hero-slide__video-play';
  button.setAttribute('aria-label', 'Play video');
  button.innerHTML =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor" opacity="0.35"/><path fill-rule="evenodd" clip-rule="evenodd" d="M13.375 9.485C14.042 9.822 14.042 10.843 13.375 11.516L8.375 14.346C7.708 14.685 7 14.178 7 13.331V6.706C7 5.859 7.708 5.352 8.375 5.69L13.375 9.485Z" fill="#fff"/></svg>';
  button.classList.toggle('hidden', !video.paused);

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    video.play().catch(() => {});
  });

  video.addEventListener('pause', () => button.classList.remove('hidden'));
  video.addEventListener('playing', () => button.classList.add('hidden'));

  wrapper.appendChild(button);
}

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    clearTimeout(this.#disabledSafetyTimer);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    if (this.#cloneStart || this.#cloneEnd) {
      const { scroller } = this.refs;
      scroller?.removeEventListener('touchstart', this.#handleTouchStart);
      scroller?.removeEventListener('touchend', this.#handleTouchEnd);
      scroller?.removeEventListener('touchcancel', this.#handleTouchEnd);
      clearTimeout(this.#autoplaySafetyTimer);
      clearTimeout(this.#touchEndGraceTimer);
      clearTimeout(this.#touchActiveSafetyTimer);
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
      window.removeEventListener('pageshow', this.#handlePageShow);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    const isWrap = requestedIndex < 0 || requestedIndex > lastIndex;

    // When loop-clones are enabled, a boundary wrap doesn't need the
    // placeholder + reorder trick below: a clone of the target slide is
    // already sitting exactly where the real slide would otherwise need to
    // be relocated to (immediately before the first / after the last real
    // slide), so we can just animate to that clone - a short, natural
    // adjacent-slide scroll - and let #resolveClonePosition silently correct
    // to the real slide once the scroll settles. This avoids ever moving a
    // real slide into a slot a clone already occupies.
    if (this.loopClones && this.infinite && isWrap && !instant) {
      const cloneTarget = requestedIndex < 0 ? this.#cloneStart : this.#cloneEnd;

      if (cloneTarget) {
        await this.#scroll.finished; // ensure we're not mid-scroll

        const targetSlide = slides[index];
        if (!targetSlide) return;

        targetSlide.setAttribute('aria-hidden', 'false');

        this.#scroll.to(cloneTarget, { instant: false });

        this.current = index;
        this.#centerSelectedThumbnail(index, 'smooth');

        this.dispatchEvent(
          new SlideshowSelectEvent({
            index,
            previousIndex: current,
            userInitiated: event != null,
            trigger: 'select',
            slide: targetSlide,
            id: targetSlide.getAttribute('slide-id'),
          })
        );

        return;
      }
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      // Unconditional ceiling, same reasoning as #touchActiveSafetyTimer:
      // both of the awaits below block on this.#scroll.finished, and every
      // select()-blocking flag in this file that has no such ceiling has
      // been observed getting stuck true on Android's unreliable scroll
      // settling. #disabled blocking forever is worse than most - it also
      // blocks the on:click actions behind pause()/play(), since those go
      // through the same disabled check - so the whole carousel, including
      // its own play/pause button, goes dead until the page is reloaded.
      clearTimeout(this.#disabledSafetyTimer);
      this.#disabledSafetyTimer = window.setTimeout(() => {
        this.#disabled = false;
      }, 2500);
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;
        clearTimeout(this.#disabledSafetyTimer);

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    // The declarative on:click action system (see component.js) always
    // calls the bound method with the triggering event as the first
    // positional argument - on:click="/play" on the play/unpause button
    // therefore calls this as play(clickEvent), not play(). Every other
    // button-invokable method here (next, previous, select) is written to
    // expect that and takes the event as its first param; play() alone
    // takes a numeric interval there instead. Passing a PointerEvent
    // straight into setInterval's delay coerces to 0, making it fire
    // essentially continuously instead of every autoplayInterval ms.
    const resolvedInterval = typeof interval === 'number' ? interval : this.autoplayInterval;

    this.paused = false;

    this.#interval = setInterval(() => {
      // Only let :hover pause autoplay on devices with a real mouse/trackpad.
      // On touch devices, tapping/swiping an element can leave it matching
      // :hover indefinitely (no mouseleave ever fires to clear it), which
      // would otherwise silently stop autoplay from ever advancing again
      // after a single swipe - while the interval itself keeps running,
      // giving no visible sign anything is wrong.
      if ((supportsHover.matches && this.matches(':hover')) || document.hidden) return;

      this.next();
    }, resolvedInterval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);

    // Safari can silently pause an autoplaying background video once its
    // slide scrolls far enough off-screen (a memory/battery optimization),
    // and does not resume it on its own when the slide scrolls back into
    // view - it just sits there paused, sometimes surfacing Safari's own
    // "tap to play" affordance instead. Re-asserting play() whenever a
    // slide becomes current again mirrors this theme's own existing "force
    // autoplay for safari" fix (see DeferredMedia.loadContent in
    // assets/media.js).
    //
    // video.hero-slide__media also matches here: the hero carousel only
    // marks its FIRST slide's video `autoplay` (so the browser doesn't
    // eagerly fetch every other slide's video on page load - see
    // _hero-slide.liquid), so later slides' videos never carry the
    // `autoplay` attribute at all and need this class-based match to start
    // playing the first time their slide becomes current.
    //
    // A slide with a video can carry TWO video elements at once - one for
    // desktop, one for mobile (_hero-slide.liquid's "custom mobile media"
    // option) - with CSS show/hiding whichever one doesn't match the
    // current viewport. video.hero-slide__media matches both indiscrimin-
    // ately, so without the viewport check below, every slide change
    // called play() on BOTH - fully downloading and decoding the hidden
    // one for nothing, every time. Only the wrapper actually visible for
    // the current viewport is eligible; anything already playing on the
    // now-wrong side gets paused instead of left running off-screen.
    const isMobileViewport = matchMedia('(max-width: 749px)').matches;

    slides?.[value]?.querySelectorAll('video[autoplay], video.hero-slide__media').forEach((video) => {
      if (!(video instanceof HTMLVideoElement)) return;

      const wrapper = video.closest('.hero-slide__media-wrapper');
      const isDesktop = wrapper?.classList.contains('hero-slide__media-wrapper--desktop') ?? true;
      const isMobile = wrapper?.classList.contains('hero-slide__media-wrapper--mobile') ?? true;
      const eligible = (isDesktop && isMobile) || (isMobileViewport ? isMobile : isDesktop);

      if (eligible) {
        ensureVideoPlayFallback(video);
        if (video.paused) attemptPlayVideo(video);
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  /**
   * Whether boundary slides should be cloned so native (touch) scrolling can
   * loop past the first/last slide, not just programmatic next()/previous()/
   * select() calls. Opt-in and separate from `infinite` because `infinite` is
   * the default for every slideshow in the theme; this changes DOM structure
   * (adds cloned nodes) and is only safe where a caller has explicitly asked
   * for it.
   */
  get loopClones() {
    return this.hasAttribute('loop-clones');
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * Unconditional ceiling on how long #disabled can stay true - see the
   * comment where it's armed, in select()'s placeholder+reorder branch.
   * @type {number | undefined}
   */
  #disabledSafetyTimer;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Clone of the last real slide, prepended before the first real slide, so
   * scrolling backward past the first slide has real content to scroll into.
   * @type {HTMLElement | undefined}
   */
  #cloneStart;

  /**
   * Clone of the first real slide, appended after the last real slide, so
   * scrolling forward past the last slide has real content to scroll into.
   * @type {HTMLElement | undefined}
   */
  #cloneEnd;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Safety-net timer id for autoplay suspend/resume (see #handleTouchStart).
   * @type {number | undefined}
   */
  #autoplaySafetyTimer;

  /**
   * Whether a touch is currently down on the scroller (between touchstart
   * and touchend/touchcancel) - not just whether the scroller is moving.
   * Native touch scrolling never sets #dragging (that's mouse-only), so
   * without this, #resolveClonePosition had no way to tell a genuine
   * scroll-end apart from a brief pause mid-swipe: a real finger-drag
   * routinely has micro-pauses longer than the scroll-end debounce, and
   * firing an instant correction into an still-active drag fought the
   * user's own finger, producing a visible flash/skip mid-swipe (confirmed
   * via frame-by-frame capture: slide jumped forward two slides then
   * snapped back to the start, all while still swiping). Once touchend
   * fires, momentum scrolling continues natively and its eventual real
   * scroll-end is left alone, same as before.
   * @type {boolean}
   */
  #touchActive = false;

  /**
   * Grace-period timer id, started on touchend/touchcancel, that delays
   * clearing #touchActive by a beat instead of clearing it immediately.
   * Captured device logs showed Android Chrome (not iOS Safari, which
   * never does this) firing a spurious touchend + immediate re-touchstart
   * mid-swipe while the finger never actually left the screen - each of
   * those brief gaps let #touchActive flip false for a moment, which was
   * long enough for #resolveClonePosition to sneak in an instant scroll
   * correction; that JS-driven scroll mutation is itself what appears to
   * make Android cancel and re-acquire the gesture, repeating the cycle
   * and producing the multi-slide skips. Keeping #touchActive engaged
   * through this short window absorbs those spurious gaps.
   * @type {number | undefined}
   */
  #touchEndGraceTimer;

  /**
   * Unconditional ceiling on how long #touchActive can stay true, armed
   * once when a touch chain begins and never re-armed by subsequent
   * touchstarts within it. Without this, a long enough run of Android's
   * spurious touchstart/touchend micro-cycles (see #touchEndGraceTimer) can
   * each re-arm that 500ms grace timer before it ever fires, leaving
   * #touchActive stuck true indefinitely - which permanently blocks
   * #resolveClonePosition and parks the user on an inert boundary clone
   * with no further real content to scroll into, i.e. the carousel goes
   * completely dead after a swipe or two. This forces #touchActive false
   * no matter what, the same "can never get permanently stuck" guarantee
   * #autoplaySafetyTimer already gives autoplay.
   * @type {number | undefined}
   */
  #touchActiveSafetyTimer;

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    // Insert boundary clones (if opted in) now that the Scroller exists, so
    // we can immediately correct the scroll position past the prepended
    // clone below - before the browser ever paints. A prepended clone of
    // the last slide shifts every real slide's offset right by its own
    // width; without an immediate correction the scroller's default
    // scrollLeft (0) lands on that clone - a visual duplicate of the LAST
    // slide - and stays there until the async initial-slide positioning
    // logic further down corrects it on a later frame, producing a visible
    // flash of the wrong slide followed by a jump back to the first slide
    // on every page load.
    if (this.loopClones && this.infinite) {
      this.#setupLoopClones();
    }

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('pageshow', this.#handlePageShow);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Prepends a clone of the last real slide before the first real slide, and
   * appends a clone of the first real slide after the last real slide, so
   * native scrolling (touch swipes, trackpad, mouse wheel) has real content
   * to scroll into past either boundary instead of hitting a hard stop.
   *
   * Clones are stripped of `ref`/`id` attributes so they're invisible to
   * this component's ref system (`this.refs.slides`) and never collide with
   * real element ids. `#resolveClonePosition` detects when a clone has
   * settled into view and silently jumps to the equivalent real slide.
   */
  #setupLoopClones() {
    const { scroller } = this.refs;
    const realSlides = this.refs.slides;
    if (!scroller || !realSlides || realSlides.length < 2) return;

    const first = realSlides[0];
    const last = realSlides[realSlides.length - 1];

    if (!first || !last) return;

    const cloneOfLast = /** @type {HTMLElement} */ (last.cloneNode(true));
    const cloneOfFirst = /** @type {HTMLElement} */ (first.cloneNode(true));

    for (const clone of [cloneOfLast, cloneOfFirst]) {
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('ref');
      clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    }

    scroller.insertBefore(cloneOfLast, first);
    scroller.appendChild(cloneOfFirst);

    this.#cloneStart = cloneOfLast;
    this.#cloneEnd = cloneOfFirst;

    // Prepending cloneOfLast just shifted every real slide's offset right by
    // its own width, so the scroller's still-default scrollLeft (0) is now
    // sitting on the clone instead of the real first slide. Correct it here,
    // synchronously, before this task yields - otherwise the browser paints
    // the clone (a duplicate of the LAST slide) first, and the async
    // initial-slide positioning further down in #setupSlideshow only fixes
    // it a frame (or more) later, producing a visible flash of the wrong
    // slide on every page load.
    this.#scroll?.to(first, { instant: true });

    // Mirror the trust-badges carousel's autoplay behavior exactly: suspend
    // on touchstart, resume on touchend, so autoplay always picks back up
    // the instant a swipe ends rather than trailing behind scroll-snap
    // settling. Deliberately plain Touch Events, not Pointer Events - once a
    // touch turns into an actual scroll, some browsers fire pointercancel
    // early or inconsistently instead of a clean pointerup, which could
    // leave autoplay suspended with nothing left to resume it. Touch events
    // don't have that ambiguity: touchend/touchcancel reliably fire for the
    // original target regardless of what the browser did with the scroll.
    // #autoplaySafetyTimer is an unconditional backstop on top of that: if a
    // touchend/touchcancel is ever missed for any reason, autoplay force-
    // resumes a few seconds later regardless, so it can never get
    // permanently stuck.
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    // A tap on the pause/play/arrow/dot controls (they sit inside the same
    // swipeable scroller, floating on top of the media) is not a swipe -
    // it has its own on:click action that calls pause()/play()/select()
    // directly. Running the swipe-suspend/resume logic below for it too
    // fought with that explicit call (confirmed: tapping play/pause became
    // unreliable specifically on Android, where the touch sequence below is
    // already flaky - see #touchEndGraceTimer), so bail out entirely here
    // and let the control's own handler own the state change.
    if (event.target instanceof Element && event.target.closest('.slideshow-control')) return;

    clearTimeout(this.#touchEndGraceTimer);
    if (!this.#touchActive) {
      // Only arm on the leading edge of a touch chain - see
      // #touchActiveSafetyTimer's doc comment for why this must NOT be
      // reset by every touchstart the way #touchEndGraceTimer is.
      clearTimeout(this.#touchActiveSafetyTimer);
      this.#touchActiveSafetyTimer = window.setTimeout(() => {
        this.#touchActive = false;
      }, 2000);
    }
    this.#touchActive = true;
    this.suspend();
    clearTimeout(this.#autoplaySafetyTimer);
    this.#autoplaySafetyTimer = window.setTimeout(() => this.resume(), 3000);
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.target instanceof Element && event.target.closest('.slideshow-control')) return;

    clearTimeout(this.#autoplaySafetyTimer);
    this.resume();

    // See #touchEndGraceTimer's doc comment - delay clearing #touchActive
    // instead of doing it immediately, so a spurious same-gesture
    // touchstart on Android has a beat to arrive and cancel this timer
    // before #resolveClonePosition is ever allowed to run. 500ms, not a
    // smaller number: captured device logs showed real gaps between a
    // spurious touchend and the next touchstart of the same gesture as
    // large as ~310ms, so a shorter window still let some of them through.
    clearTimeout(this.#touchEndGraceTimer);
    this.#touchEndGraceTimer = window.setTimeout(() => {
      this.#touchActive = false;
      clearTimeout(this.#touchActiveSafetyTimer);
    }, 500);
  };

  /**
   * When loop-clones are enabled, checks whether the scroll has settled on a
   * cloned boundary slide and, if so, silently (instantly, no animation)
   * repositions to the equivalent real slide and syncs `current`/dots/counter
   * to match. This makes the loop invisible to the user: native touch/scroll
   * physics are free to carry the user past either boundary into a clone,
   * and this corrects the position right after the scroll settles.
   */
  #resolveClonePosition = () => {
    if (!this.loopClones || !this.infinite) return;
    if (!this.#cloneStart && !this.#cloneEnd) return;
    if (!this.#scroll) return;
    // A mouse drag is still in progress. #handleMouseDown calls
    // this.#scroll.by() on every pointermove, and if the mouse briefly
    // pauses mid-drag (normal human movement), the scroll-end debounce can
    // fire while the drag is still active - snapping the scroll position
    // out from under it would fight the drag and look like a glitch.
    // onPointerUp already runs its own wrap-to-clone-position logic once the
    // drag genuinely ends, so skipping this here doesn't leave anything
    // unresolved.
    if (this.#dragging) return;

    // Same problem, touch equivalent: a finger is still down. See
    // #touchActive's own doc comment for why this needs its own guard
    // separate from #dragging.
    if (this.#touchActive) return;

    const { scroller } = this.refs;
    const { slides } = this;
    if (!scroller || !slides?.length) return;

    const { axis } = this.#scroll;
    const scrollerCenter = center(scroller, axis);

    /** @param {HTMLElement | undefined} clone */
    const isCentered = (clone) => {
      if (!clone) return false;
      const rect = clone.getBoundingClientRect();
      const size = axis === 'x' ? rect.width : rect.height;
      if (!size) return false;
      return Math.abs(center(clone, axis) - scrollerCenter) < size / 2;
    };

    let targetIndex = -1;
    if (isCentered(this.#cloneStart)) {
      targetIndex = slides.length - 1;
    } else if (isCentered(this.#cloneEnd)) {
      targetIndex = 0;
    }

    if (targetIndex === -1) return;

    const targetSlide = slides[targetIndex];
    if (!targetSlide) return;

    const previousIndex = this.current;

    // This method runs synchronously inside the Scroller's own onScrollEnd
    // callback (see #onTransitionEnd), while its internal #isScrolling flag
    // is still true (it only flips back to false after onScrollEnd
    // returns). Calling this.#scroll.to() here fires a new native 'scroll'
    // event of its own - and depending on whether the browser dispatches
    // that event synchronously or defers it a tick (this differs enough
    // across browsers/devices that it isn't reliably one or the other),
    // it can re-enter the Scroller's start/end machinery a second time
    // while the first pass hasn't finished unwinding: re-adding
    // [transitioning] and re-running this very check against a layout that
    // hasn't settled yet. That self-inflicted nested cycle - not any single
    // device's touch handling - is what produced the inconsistent stuck/
    // skipped-slide reports across trackpad, iOS and Android alike.
    // Deferring to a microtask lets the current onScrollEnd call finish
    // completely (including the Scroller flipping #isScrolling back to
    // false) before this correction runs as its own clean, top-level
    // scroll rather than one nested inside another.
    queueMicrotask(() => {
      if (!this.#scroll) return;

      this.#scroll.to(targetSlide, { instant: true });

      // If current already matches (e.g. select() already updated state before
      // animating to this clone), the caller already dispatched a select event -
      // only the scroll position needed correcting, so stop here.
      if (previousIndex === targetIndex) return;

      this.current = targetIndex;
      this.#centerSelectedThumbnail(targetIndex, 'instant');

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: targetIndex,
          previousIndex,
          userInitiated: true,
          trigger: 'scroll',
          slide: targetSlide,
          id: targetSlide.getAttribute('slide-id'),
        })
      );
    });
  };

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#resolveClonePosition();
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    // #handleScroll calls #sync() on every throttled native 'scroll' tick
    // while the user is dragging - still ~20x/second even throttled (see
    // Scroller's SCROLL_END_TIMEOUT) - and most of those ticks land on the
    // same closest slide as the tick before, since crossing a slide
    // boundary is a small fraction of the whole gesture. Reassigning
    // `current` unconditionally on every one of those ticks re-ran its full
    // side-effect pass every time - aria-selected/disabled DOM writes, plus
    // a querySelectorAll + play()/pause() sweep over the slide's <video>
    // elements - none of which had actually changed. That's pure overhead
    // stacked on top of the swipe's own compositor work, and heavy enough
    // on mid-range Android hardware to visibly stutter the swipe itself -
    // most noticeable on slides with a <video>, since a decoding video is
    // already competing for the same main thread. Only invoke the setter
    // when the centered slide has actually changed.
    if (index === this.#current) return index;

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.suspend();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const rawIndex = next + modifier;
      const lastIndex = slides.length - 1;

      // A drag past the first/last slide has no more real content to scroll to.
      // With looping enabled, wrap the index (last -> first, first -> last) instead
      // of clamping to the boundary. This jump is instant rather than animated:
      // select()'s smooth placeholder/reorder transition is built for clean
      // programmatic calls (arrows, dots) on a settled carousel, and calling it
      // here while the drag's own scroll is still resolving caused a visible
      // glitch (confirmed by inspecting scrollLeft/scrollWidth mid-transition).
      // An instant cut to the wrapped slide has no such race.
      let newIndex = rawIndex;
      let didWrap = false;
      if (rawIndex < 0) {
        if (!this.infinite) {
          newIndex = 0;
        } else {
          newIndex = lastIndex;
          didWrap = true;
        }
      } else if (rawIndex > lastIndex) {
        if (!this.infinite) {
          newIndex = lastIndex;
        } else {
          newIndex = 0;
          didWrap = true;
        }
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#scroll.to(newSlide, didWrap ? { instant: true } : undefined);

      this.#centerSelectedThumbnail(newIndex, didWrap ? 'instant' : 'smooth');

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Re-asserts playback for the current slide's video(s) on `pageshow`.
   * This event fires after every load AND whenever iOS Safari restores the
   * page from the back/forward cache (bfcache) - which is what a "pull to
   * refresh"/reload on iOS Safari sometimes actually does instead of a full
   * network reload. Either way, a background video that was mid-playback
   * when the page was cached/reloaded can come back on iOS Safari paused on
   * a frozen frame, with nothing else left to re-trigger play() - the
   * `autoplay` attribute and the `current` setter's own play() call (see
   * its "force autoplay for safari" logic above) only ever fire once, at
   * setup, and iOS Safari doesn't reliably honor that first attempt across
   * a reload/bfcache restore. Reassigning `current` to itself re-runs that
   * exact same play()/pause() pass without duplicating its logic here.
   */
  #handlePageShow = () => {
    // #current is plain in-memory JS state - it always starts back at 0 on
    // a real reload (a bfcache restore is the only case where it survives
    // as-is), regardless of which slide the user had actually swiped to
    // before refreshing. Meanwhile iOS Safari/Chrome commonly restore the
    // scroller's real scroll position (i.e. which slide is visually shown)
    // across that same reload independently of JS, and don't reliably fire
    // a 'scroll' event for that silent restoration - so #current can be
    // left pointing at the wrong slide entirely (e.g. still 0 while slide 3
    // is what's actually on screen) with nothing having ever re-synced it.
    // #sync() reads the real, current layout (via the IntersectionObserver-
    // backed `visibleSlides`) rather than trusting that stale index, so it
    // corrects this first.
    this.#sync();

    // Whether or not #sync() found a mismatch to correct, also force the
    // setter's video play()/pause() pass to run again for whatever slide it
    // landed on. This is what actually recovers the frozen-on-reload video
    // even when the index was already right and only playback itself
    // silently failed to (re)start.
    this.current = this.#current;
  };

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}